import AllInclusiveRounded from "@mui/icons-material/AllInclusiveRounded";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import TodayRounded from "@mui/icons-material/TodayRounded";
import { Box, Button, Typography } from "@mui/material";
import { toZonedTime } from "date-fns-tz";
import type { FC, ReactElement, ReactNode } from "react";
import { TimelinePill } from "../../../modules/timeline/components/timeline-pill";
import { formatInterval } from "../../../utils/interval";
import { formatDatetime, type INTERVAL, type ISO8601 } from "../../../utils/iso8601";
import { type MomentCardMoment } from "../types";

export type MomentWhenClassification =
  | {
      type: "immediate";
      publishedAt: ISO8601 | null;
      timezone: string | null;
    }
  | {
      type: "scheduled";
      when: string;
      timezone: string | null;
    }
  | {
      type: "triggered";
      interval: INTERVAL | null;
      eventKey: string;
      followUp?: MomentCardMoment["follow_up"];
    }
  | {
      type: "unscheduled";
      timezone: string | null;
    };

export type WhenProps = {
  moment: {
    immediate: boolean | null;
    published_at: ISO8601 | null;
    timezone: string | null;
    schedule?: {
      when: string | null;
      event_key: string | null;
      interval: INTERVAL | null;
    } | null;
    follow_up?: MomentCardMoment["follow_up"];
  };
};

export function classifyMomentWhen(moment?: WhenProps["moment"]): MomentWhenClassification {
  // Priority 1: Immediate
  if (moment?.immediate) {
    return {
      type: "immediate",
      publishedAt: moment.published_at,
      timezone: moment.timezone,
    };
  }

  // Priority 2: Scheduled (Specific Time)
  if (moment?.schedule?.when) {
    return {
      type: "scheduled",
      when: moment.schedule.when,
      timezone: moment.timezone,
    };
  }

  // Priority 3: Triggered (Event based)
  if (moment?.schedule?.event_key) {
    return {
      type: "triggered",
      eventKey: moment.schedule.event_key,
      interval: moment.schedule.interval,
      followUp: moment.follow_up,
    };
  }

  return {
    type: "unscheduled",
    timezone: moment?.timezone ?? null,
  };
}

export function renderMomentWhenIcon(c: MomentWhenClassification): ReactElement {
  const sx = { verticalAlign: "middle", mr: 1 };

  if (c.type === "triggered") {
    return <AllInclusiveRounded sx={sx} />;
  }

  // For Immediate, Scheduled, and Unscheduled, we fall back to timezone logic
  // Note: c.timezone is available on all these types, but not on 'triggered'
  // strictly speaking, but our union definition excludes it from triggered.
  if (c.timezone) return <TodayRounded sx={sx} />;

  return <CalendarMonth sx={sx} />;
}

export function renderMomentWhenContent(c: MomentWhenClassification): ReactNode {
  switch (c.type) {
    case "immediate": {
      if (!c.publishedAt) return "Not scheduled";
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return `${formatDatetime(new Date(toZonedTime(c.publishedAt, userTimeZone)))} (${userTimeZone})`;
    }

    case "scheduled": {
      const timezoneLabel = c.timezone ? ` (${c.timezone})` : " (Recipient’s local time zone)";
      return formatDatetime(new Date(c.when)) + timezoneLabel;
    }

    case "triggered": {
      const href = c.followUp
        ? `/moment/${c.followUp.config.source_id}`
        : `/timeline/${c.eventKey}`;

      const label = c.followUp ? c.followUp.config.moment_title : c.eventKey;

      const analyticsId = c.followUp ? "moment-card-moment-pill" : "moment-card-event-pill";

      return (
        <Box sx={{ display: "inline-block" }} component="span">
          {formatInterval(c.interval)}&nbsp;
          <Button data-analytics-id={analyticsId} href={href} sx={{ m: 0, p: 0 }}>
            <TimelinePill>{label}</TimelinePill>
          </Button>
        </Box>
      );
    }

    case "unscheduled":
    default:
      return "Not scheduled";
  }
}

export const When: FC<WhenProps> = ({ moment }) => {
  const classification = classifyMomentWhen(moment);

  return (
    <>
      <Typography align="left" fontSize={12} color="rgb(180, 180, 180)">
        When
      </Typography>
      <Box data-testid="when" sx={{ display: "inline-block" }}>
        {renderMomentWhenIcon(classification)}
        {renderMomentWhenContent(classification)}
      </Box>
    </>
  );
};
